/**
 * useOwnerClientTarget: the ONE "Applies to" picker state for settings a 3PL
 * owner edits on a client's behalf (Print size rules, Designs source, Platen
 * size). Moved verbatim out of `PrintSizeRulesSection` so there is no third
 * copy of the dedupe / sort / default-to-own-org rules.
 *
 *   - `clientOptions`: active junction clients from `bridgeControl.listClients`
 *     (the owner's roster across its owned locations), deduped by client org
 *     (a client with two location junctions otherwise appears twice) and
 *     sorted by name, case-insensitively.
 *   - `selectedOrgId`: defaults to the caller's own org once it is known.
 *   - `targetOrgInput`: `undefined` for the own org (the routers then default
 *     to `ctx.organizationId`), the client org id otherwise. Pass it as the
 *     `organizationId` input to every read and write the section makes.
 *
 * The server is authoritative (`assertOwnerOrSelf` on every target-org
 * procedure); this hook only decides which org the UI asks about.
 */
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';

export interface OwnerClientOption {
  id: string;
  name: string;
}

export interface OwnerClientTarget {
  ownOrgId: string | undefined;
  clientOptions: OwnerClientOption[];
  clientsQuery: ReturnType<typeof trpc.bridgeControl.listClients.useQuery>;
  selectedOrgId: string | undefined;
  setSelectedOrgId: (orgId: string) => void;
  isOwnOrgSelected: boolean;
  targetOrgInput: string | undefined;
}

export function useOwnerClientTarget(): OwnerClientTarget {
  const { organization } = useOrganization();
  const ownOrgId = organization?.id;

  const clientsQuery = trpc.bridgeControl.listClients.useQuery();
  const clientOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of clientsQuery.data?.clients ?? []) {
      if (byId.has(c.clientOrganizationId)) continue;
      byId.set(c.clientOrganizationId, c.clientAlias ?? c.clientName ?? c.clientOrganizationId);
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [clientsQuery.data]);

  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedOrgId && ownOrgId) setSelectedOrgId(ownOrgId);
  }, [selectedOrgId, ownOrgId]);

  const isOwnOrgSelected = !selectedOrgId || selectedOrgId === ownOrgId;
  const targetOrgInput = isOwnOrgSelected ? undefined : selectedOrgId;

  return {
    ownOrgId,
    clientOptions,
    clientsQuery,
    selectedOrgId,
    setSelectedOrgId,
    isOwnOrgSelected,
    targetOrgInput,
  };
}
